import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { catchError, EMPTY, finalize, tap } from 'rxjs';
import { ApiErrorResponse } from '../../core/http/api-error.model';
import { extractApiError } from '../../core/http/api-error.util';
import { UiMessageService } from '../../core/ui/message/ui-message.service';
import { InitAuth } from '../auth/auth.actions';
import * as A from './subscriptions.actions';
import { Subscription, SubscriptionsStateModel } from './subscriptions.models';
import { SubscriptionsService } from './subscriptions.service';

const initial: SubscriptionsStateModel = {
    plans: [],
    billing: 'monthly',
    current: undefined,
    error: null,
    loading: false
};

@State<SubscriptionsStateModel>({
    name: A.stateName,
    defaults: initial
})
@Injectable()
export class SubscriptionsState {
    constructor(
        private api: SubscriptionsService,
        private uiMessage: UiMessageService
    ) {}

    @Selector() static plans(s: SubscriptionsStateModel) {
        return s?.plans ?? [];
    }
    @Selector() static billing(s: SubscriptionsStateModel) {
        return s?.billing ?? 'monthly';
    }
    @Selector() static current(s: SubscriptionsStateModel) {
        return s?.current ?? undefined;
    }
    @Selector() static loading(s: SubscriptionsStateModel) {
        return s?.loading ?? false;
    }
    @Selector() static error(s: SubscriptionsStateModel) {
        return s?.error ?? null;
    }
    @Selector() static errorMessage(s: SubscriptionsStateModel) {
        return s?.error?.message ?? s?.error?.code ?? null;
    }

    /* =========================
     LOAD PLANS
  ========================= */

    @Action(A.LoadPlans)
    loadPlans(ctx: StateContext<SubscriptionsStateModel>) {
        ctx.patchState({ loading: true, error: null });
        return this.api.getPlans().pipe(
            tap((plans) => {
                ctx.patchState({ plans });
            }),
            catchError((err: ApiErrorResponse) => {
                const apiErr = err?.apiError ?? extractApiError(err);
                ctx.patchState({ error: apiErr });
                this.uiMessage.error(this.uiMessage.apiErrorMessage(apiErr, 'No se pudieron cargar los planes.'));
                return EMPTY;
            }),
            finalize(() => ctx.patchState({ loading: false }))
        );
    }

    @Action(A.SetBilling)
    setBilling(
        ctx: StateContext<SubscriptionsStateModel>,
        { billing }: A.SetBilling
    ) {
        ctx.patchState({ billing });
    }

    /* =========================
     LOAD CURRENT
  ========================= */

    @Action(A.LoadCurrent)
    loadCurrent(ctx: StateContext<SubscriptionsStateModel>) {
        ctx.patchState({ loading: true, error: null });
        return this.api.getCurrent().pipe(
            tap((me: Subscription) => {
                const endIso = me?.current_period_end ?? null;
                const mapped: Subscription = {
                    ...me,
                    current_period_end: endIso,
                    period: ctx.getState().billing,
                    renewsAt: endIso ? new Date(endIso) : null
                };
                ctx.patchState({ current: mapped });
            }),
            catchError((err: ApiErrorResponse) => {
                const apiErr = err?.apiError ?? extractApiError(err);
                ctx.patchState({ error: apiErr });
                this.uiMessage.error(this.uiMessage.apiErrorMessage(apiErr, 'No se pudo cargar tu suscripción.'));
                return EMPTY;
            }),
            finalize(() => ctx.patchState({ loading: false }))
        );
    }

    /* =========================
     CHECKOUT
  ========================= */

    @Action(A.StartCheckout)
    startCheckout(
        ctx: StateContext<SubscriptionsStateModel>,
        { plan_id }: A.StartCheckout
    ) {
        const period = ctx.getState().billing;
        ctx.patchState({ loading: true, error: null });

        return this.api.startCheckout(plan_id, period).pipe(
            tap((resp) => {
                if (resp?.payment) {
                    this.api.submitRedsysForm(resp.payment);
                } else {
                    ctx.dispatch(new A.LoadCurrent());
                }
            }),
            catchError((err: ApiErrorResponse) => {
                const apiErr = err?.apiError ?? extractApiError(err);
                ctx.patchState({ error: apiErr });
                this.uiMessage.error(this.uiMessage.apiErrorMessage(apiErr, 'No se pudo iniciar el checkout.'));
                return EMPTY;
            }),
            finalize(() => ctx.patchState({ loading: false }))
        );
    }

    /* =========================
     TRIAL
  ========================= */

    @Action(A.StartTree)
    startTree(
        ctx: StateContext<SubscriptionsStateModel>,
        { plan_id }: A.StartTree
    ) {
        ctx.patchState({ loading: true, error: null });
        return this.api.startTree(plan_id).pipe(
            tap(() => {
                ctx.dispatch([new InitAuth(), new A.LoadCurrent()]);
            }),
            catchError((err: ApiErrorResponse) => {
                const apiErr = err?.apiError ?? extractApiError(err);
                ctx.patchState({ error: apiErr });
                this.uiMessage.error(this.uiMessage.apiErrorMessage(apiErr, 'No se pudo iniciar el periodo de prueba.'));
                return EMPTY;
            }),
            finalize(() => ctx.patchState({ loading: false }))
        );
    }

    /* =========================
     CANCEL (downgrade)
  ========================= */

    @Action(A.CancelSubscription)
    cancel(ctx: StateContext<SubscriptionsStateModel>) {
        ctx.patchState({ loading: true, error: null });
        return this.api.cancel().pipe(
            tap(() => {
                // backend es la fuente de verdad
                ctx.dispatch(new A.LoadCurrent());
            }),
            catchError((err: ApiErrorResponse) => {
                const apiErr = err?.apiError ?? extractApiError(err);
                ctx.patchState({ error: apiErr });
                this.uiMessage.error(this.uiMessage.apiErrorMessage(apiErr, 'No se pudo cancelar la suscripción.'));
                return EMPTY;
            }),
            finalize(() => ctx.patchState({ loading: false }))
        );
    }

    @Action(A.ResetSubscriptions)
    reset(ctx: StateContext<SubscriptionsStateModel>) {
        ctx.setState(initial);
        return EMPTY;
    }
}
